// frontend/src/components/asset/VehicleInspectionDetailModal.js
/**
 * Custodian drill-down for a single monthly vehicle inspection.
 *
 * Shared by:
 *   - AssetAdminFleetInspections (fleet-wide exceptions / compliance calendar)
 *   - AssetDetailModal → Inspection History tab (per-vehicle dossier)
 *
 * Checklist answers stay read-only; custodians can correct a bad odometer
 * reading (clear preferred) via CorrectInspectionOdometerModal.
 *
 * Theme: Admin (red). Secure fault photos load via authenticated blob fetch.
 * `onViewVehicle` is optional — omit when already viewing that vehicle.
 */

import React, { useState, useEffect } from 'react';
import { ClipboardCheck, AlertCircle, Gauge, FileText, Car } from 'lucide-react';
import Modal from '../UI/Modal';
import { StatusBadge } from '../UI/StatusBadge';
import api from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import { useVehicleInspectionDetail } from '../../hooks/useAssetInventoryData';
import CorrectInspectionOdometerModal from './CorrectInspectionOdometerModal';

/**
 * odometer_raw is the uncleaned entry/import string (e.g. "284230 km").
 * Only surface it when it adds audit signal beyond the canonical km —
 * not when it's the same digits with different formatting (95,725 vs 95725).
 */
const shouldShowOdometerRaw = (raw, km) => {
  const text = String(raw || '').trim();
  if (!text) return false;
  if (km == null || km === '') return true; // cleared reading — raw is the evidence
  const rawDigits = text.replace(/[^\d]/g, '');
  if (!rawDigits) return true;
  return rawDigits !== String(Number(km));
};

const InspectionPhoto = ({ photo }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let blobUrl;
    let cancelled = false;
    if (!photo.photo_url) return undefined;
    api.get(photo.photo_url, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(res.data);
        setUrl(blobUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [photo.photo_url]);

  if (failed) {
    return (
      <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
        <AlertCircle className="w-5 h-5 text-gray-400" />
      </div>
    );
  }

  return (
    <a
      href={url || undefined}
      target="_blank"
      rel="noopener noreferrer"
      className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 block hover:ring-2 hover:ring-red-500 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
    >
      {url ? (
        <img src={url} alt="Fault evidence" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" />
        </div>
      )}
    </a>
  );
};

const VehicleInspectionDetailModal = ({ isOpen, onClose, inspectionId, onViewVehicle }) => {
  const { data: inspection, isLoading } = useVehicleInspectionDetail(
    inspectionId, { enabled: isOpen && !!inspectionId }
  );
  const [correctOdoOpen, setCorrectOdoOpen] = useState(false);
  // Odometer + free-text notes are surfaced once (hero / Driver Notes) —
  // drop those checklist rows so ops don't see the same content twice.
  const notesResult = (inspection?.results || []).find((r) => r.item_key === 'notes');
  const driverNotes = String(inspection?.notes || notesResult?.value || '').trim();
  const results = (inspection?.results || []).filter(
    (r) => r.item_key !== 'odometer' && r.item_key !== 'notes',
  );
  const hasOdometer = inspection?.odometer_km != null;
  const wasCorrected = Boolean(inspection?.odometer_corrected_at);
  const odometerNeedsReview = inspection?.odometer_status === 'review_required';
  const showAsEntered = shouldShowOdometerRaw(
    inspection?.odometer_raw,
    inspection?.odometer_km,
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Inspection Details"
        size="md"
        theme="admin"
        contentClassName="space-y-3"
        footer={
          <>
            {inspection?.asset_id && onViewVehicle && (
              <button
                type="button"
                onClick={() => onViewVehicle(inspection.asset_id)}
                className="btn btn-admin-outline"
              >
                <Car className="w-4 h-4 mr-2" />
                View Vehicle
              </button>
            )}
            {inspection && (hasOdometer || wasCorrected) && (
              <button
                type="button"
                onClick={() => setCorrectOdoOpen(true)}
                className="btn btn-admin-outline"
              >
                <Gauge className="w-4 h-4 mr-2" />
                Correct Odometer
              </button>
            )}
            <button type="button" onClick={onClose} className="btn btn-admin-outline">
              Close
            </button>
          </>
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <ClipboardCheck className="w-4 h-4 text-red-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-sm font-semibold text-gray-900 truncate">{inspection?.vehicle_number}</h2>
                  <StatusBadge status={inspection?.status} label={inspection?.status_display} />
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {inspection?.vehicle_type_display}
                  {inspection?.inspection_date && ` · ${formatDate(inspection.inspection_date)}`}
                  {inspection?.submitted_by_name && ` · by ${inspection.submitted_by_name}`}
                </p>
              </div>
            </div>

            {(hasOdometer || wasCorrected) && (
              <div className={`rounded-xl border p-3 space-y-1.5 text-sm ${
                odometerNeedsReview
                  ? 'border-amber-200 bg-amber-50 text-amber-950'
                  : 'border-gray-100 bg-gray-50 text-gray-700'
              }`}
              >
                <div className="flex items-center gap-2">
                  <Gauge className={`w-4 h-4 flex-shrink-0 ${
                    odometerNeedsReview ? 'text-amber-600' : 'text-gray-400'
                  }`}
                  />
                  <span className="min-w-0">
                    Odometer:{' '}
                    {hasOdometer ? (
                      <span className="font-medium tabular-nums">
                        {Number(inspection.odometer_km).toLocaleString('en-SG')} km
                      </span>
                    ) : (
                      <span className="font-medium text-amber-800">Cleared</span>
                    )}
                    {showAsEntered ? (
                      <span className="text-xs text-gray-500">
                        {' '}· as entered {inspection.odometer_raw}
                      </span>
                    ) : null}
                  </span>
                  {odometerNeedsReview && (
                    <span className="ml-auto flex-shrink-0">
                      <StatusBadge status="review_required" />
                    </span>
                  )}
                </div>
                {odometerNeedsReview && inspection.odometer_anomaly_detail && (
                  <p className="text-xs text-amber-800 pl-6">
                    {inspection.odometer_anomaly_detail}
                  </p>
                )}
                {wasCorrected && (
                  <p className="text-xs text-gray-500 pl-6">
                    Corrected
                    {inspection.odometer_previous_km != null
                      ? ` from ${Number(inspection.odometer_previous_km).toLocaleString('en-SG')} km`
                      : ''}
                    {inspection.odometer_correction_reason
                      ? ` — ${inspection.odometer_correction_reason}`
                      : ''}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              {results.map((r) => (
                <div
                  key={r.id}
                  className={`rounded-lg border p-3 ${r.is_fail ? 'border-red-200 bg-red-50' : 'border-gray-100'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900">{r.item_label}</p>
                    <span className={`text-xs font-semibold whitespace-nowrap ${r.is_fail ? 'text-red-700' : 'text-gray-500'}`}>
                      {r.value_display}
                    </span>
                  </div>
                  {r.remarks && (
                    <p className="text-xs text-gray-600 mt-1.5 flex items-start gap-1">
                      <FileText className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      {r.remarks}
                    </p>
                  )}
                  {r.photos?.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {r.photos.map((p) => <InspectionPhoto key={p.id} photo={p} />)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {driverNotes && (
              <div className="bg-gray-50 rounded-xl p-3">
                <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  <FileText className="w-3.5 h-3.5" />
                  Driver Notes
                </h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{driverNotes}</p>
              </div>
            )}
          </>
        )}
      </Modal>

      <CorrectInspectionOdometerModal
        isOpen={correctOdoOpen}
        onClose={() => setCorrectOdoOpen(false)}
        inspection={inspection}
      />
    </>
  );
};

export default VehicleInspectionDetailModal;
